import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import {
  catchError,
  map,
  concatMap,
  exhaustMap,
  withLatestFrom,
  filter,
  switchMap,
  takeLast,
  skipUntil,
  delay,
  delayWhen,
  tap,
  buffer,
  bufferWhen,
  bufferToggle,
} from "rxjs/operators";
import { of } from "rxjs";
import { AddOrganizationActions } from "../../actions/add-organization/add-organization.actions";
import { DropdownsService } from "@services/dropdowns.service";
import { OrganizationDetailsService } from "@services/organization-details.service";
import { selectUpdateOrganizationInfo } from "@store/selectors/add-organization/add-organization.selectors";
import { concatLatestFrom } from "@ngrx/operators";
import { Store } from "@ngrx/store";
import { selectUserOrgId } from "@store/selectors/user/user.selectors";
import { routerRequestActionTo } from "@store/actions/function-actions/router-request.action";
import { UserActions } from "@store/actions/user/user.actions";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Router } from "@angular/router";

@Injectable()
export class AddOrganizationEffects {
  getOrganizationDetailsOnIdChange$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        UserActions.setUserOrganizationId,
        AddOrganizationActions.updateOrganizationDetailsSuccess,
        AddOrganizationActions.updateOrganizationDetailsFailure
      ),
      concatLatestFrom(() => [this.store.select(selectUserOrgId)]),
      filter(([, orgId]) => {
        return orgId != null && orgId != 0;
      }),
      switchMap(([, orgId]) => {
        if (typeof orgId === "number") {
          return this.organizationService.getOrganizationDetails(orgId).pipe(
            map((data) =>
              AddOrganizationActions.getOrganizationDetailsSuccess({
                details: data,
              })
            ),
            catchError((error) =>
              of(
                AddOrganizationActions.getOrganizationDetailsFailure({ error })
              )
            )
          );
        } else {
          return of(
            AddOrganizationActions.getOrganizationDetailsFailure({
              error: "orgId is null",
            })
          );
        }
      })
    );
  });

  getStatesDropdownOnLoad$ = createEffect(() => {
    return this.actions$.pipe(
      routerRequestActionTo("/organization"),
      switchMap(() => {
        return this.dropdownsService.getStates("").pipe(
          map((data) =>
            AddOrganizationActions.getStatesSuccess({ contents: data })
          ),
          catchError((error) =>
            of(AddOrganizationActions.getStatesFailure({ error }))
          )
        );
      })
    );
  });

  getStatesDropdownOnSearch$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AddOrganizationActions.stateSearchChanged),
      switchMap((action) => {
        return this.dropdownsService.getStates(action.value).pipe(
          map((data) =>
            AddOrganizationActions.getStatesSuccess({ contents: data })
          ),
          catchError((error) =>
            of(AddOrganizationActions.getStatesFailure({ error }))
          )
        );
      })
    );
  });

  getOrgTypesDropdownOnLoad$ = createEffect(() => {
    return this.actions$.pipe(
      routerRequestActionTo("/organization"),
      switchMap(() => {
        return this.dropdownsService.getOrganizationTypes("").pipe(
          map((data) =>
            AddOrganizationActions.getOrganizationTypesSuccess({
              contents: data,
            })
          ),
          catchError((error) =>
            of(AddOrganizationActions.getOrganizationTypesFailure({ error }))
          )
        );
      })
    );
  });

  getOrgTypesDropdownOnSearch$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AddOrganizationActions.typeSearchChanged),
      switchMap((action) => {
        return this.dropdownsService.getOrganizationTypes(action.value).pipe(
          map((data) =>
            AddOrganizationActions.getOrganizationTypesSuccess({
              contents: data,
            })
          ),
          catchError((error) =>
            of(AddOrganizationActions.getOrganizationTypesFailure({ error }))
          )
        );
      })
    );
  });

  updateOrganizationDetails$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AddOrganizationActions.saveButtonPressed),
      concatLatestFrom(() => [this.store.select(selectUpdateOrganizationInfo)]),
      switchMap(([, data]) => {
        if (data.isValid) {
          return this.organizationService.updateOrganizationDetails(data).pipe(
            map((data) =>
              AddOrganizationActions.updateOrganizationDetailsSuccess({
                details: data,
              })
            ),
            catchError((error) =>
              of(
                AddOrganizationActions.updateOrganizationDetailsFailure({
                  error,
                })
              )
            )
          );
        } else {
          return of(
            AddOrganizationActions.updateOrganizationDetailsFailure({
              error: "Form Not Valid",
            })
          );
        }
      })
    );
  });

  createNewOrganization$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AddOrganizationActions.createNewOrganization),
      switchMap((action) => {
        return this.organizationService.createNewOrganization(action.organizationData).pipe(
          map((data) =>
            AddOrganizationActions.createNewOrganizationSuccess({
              details: data,
            })
          ),
          catchError((error) =>
            of(
              AddOrganizationActions.createNewOrganizationFailure({
                error,
              })
            )
          )
        );
      })
    );
  });

  createNewOrganizationSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AddOrganizationActions.createNewOrganizationSuccess),
        tap(({ details }) => {
          this.snackBar.open(
            `Organization "${details.name}" created successfully!`,
            "Close",
            { duration: 3000 }
          );

          localStorage.setItem("selectedOrgId", details.id.toString());

          this.store.dispatch(
            UserActions.setUserOrganizationId({
              id: details.id,
            })
          );

          setTimeout(() => {
            this.router.navigate(["/organization"]);
          }, 1500);
        })
      );
    },
    { dispatch: false }
  );

  createNewOrganizationFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AddOrganizationActions.createNewOrganizationFailure),
        tap(({ error }) => {
          this.snackBar.open(
            "Failed to create organization. Please try again.",
            "Close",
            { duration: 5000 }
          );
          console.error("Failed to create organization:", error);
        })
      );
    },
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private store: Store,
    private dropdownsService: DropdownsService,
    private organizationService: OrganizationDetailsService,
    private router: Router,
    private snackBar: MatSnackBar
  ) {}
}